import React, { useEffect, useMemo, useState } from 'react';
import {
  Box, Typography, Paper, Table, TableHead, TableRow,
  TableCell, TableBody, TableContainer, Chip, Button,
  CircularProgress, Stack, Dialog, DialogTitle,
  DialogContent, DialogActions, TextField, MenuItem,
  Switch, FormControlLabel, IconButton, Tooltip,
  InputAdornment,
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Search as SearchIcon,
} from '@mui/icons-material';
import { toast } from 'react-toastify';
import { payrollApi } from '../../../api/payrollApi';
import {
  SalaryComponentDto,
  CreateSalaryComponent,
  UpdateSalaryComponent,
  SalaryComponentType,
  SalaryComponentTypeLabels,
  SalaryComponentTypeColors,
  SalaryComponentConsiderAs,
  SalaryComponentConsiderAsLabels,
} from '../../../types/payrollTypes';

type FormErrors = {
  name?: string;
  label?: string;
  type?: string;
  considerAs?: string;
};

const salaryComponentTypeOptions = Object.values(SalaryComponentType)
  .filter((value): value is SalaryComponentType => typeof value === 'number')
  .map((value) => ({ value, label: SalaryComponentTypeLabels[value] }));

const getDefaultConsiderAs = (type: SalaryComponentType): SalaryComponentConsiderAs => {
  switch (type) {
    case SalaryComponentType.Deduction:
    case SalaryComponentType.TaxComponent:
    case SalaryComponentType.LeaveComponent:
      return SalaryComponentConsiderAs.Deductions;
    case SalaryComponentType.Contribution:
      return SalaryComponentConsiderAs.EmployerContributions;
    case SalaryComponentType.ReimbursableComponent:
      return SalaryComponentConsiderAs.Reimbursements;
    case SalaryComponentType.BaseComponent:
    case SalaryComponentType.VariablePay:
    case SalaryComponentType.Overtime:
    case SalaryComponentType.Earning:
    case SalaryComponentType.Allowance:
    default:
      return SalaryComponentConsiderAs.Earnings;
  }
};

const getSaveErrorMessage = (error: any): string => {
  const message =
    typeof error?.response?.data === 'string'
      ? error.response.data
      : error?.response?.data?.message;

  if (typeof message === 'string' && message.trim()) {
    if (message.toLowerCase().includes('same name already exists')) {
      return 'This component already exists.';
    }

    return message;
  }

  return 'Failed to save component';
};

const SalaryComponentsPage: React.FC = () => {
  const [components, setComponents] = useState<SalaryComponentDto[]>([]);
  const [loading, setLoading] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editItem, setEditItem] = useState<SalaryComponentDto | null>(null);
  const [errors, setErrors] = useState<FormErrors>({});
  const [searchTerm, setSearchTerm] = useState('');
  const [form, setForm] = useState<CreateSalaryComponent & { isActive: boolean }>({
    name: '',
    label: '',
    type: SalaryComponentType.Earning,
    considerAs: SalaryComponentConsiderAs.Earnings,
    isTaxable: false,
    isStatutory: false,
    isActive: true,
  });

  const loadComponents = async () => {
    setLoading(true);
    try {
      const data = await payrollApi.getSalaryComponents();
      setComponents(data);
    } catch {
      toast.error('Failed to load salary components');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    void loadComponents();
  }, []);

  const filteredComponents = useMemo(() => {
    const search = searchTerm.trim().toLowerCase();
    if (!search) return components;

    return components.filter((component) => {
      const values = [
        component.name,
        component.label,
        SalaryComponentTypeLabels[component.type],
        SalaryComponentConsiderAsLabels[component.considerAs],
        component.isTaxable ? 'yes taxable' : 'no non taxable',
        component.isStatutory ? 'yes statutory' : 'no non statutory',
        component.isActive ? 'active' : 'inactive',
      ];

      return values.some((value) => value.toLowerCase().includes(search));
    });
  }, [components, searchTerm]);

  const openCreate = () => {
    setEditItem(null);
    setErrors({});
    setForm({
      name: '',
      label: '',
      type: SalaryComponentType.Earning,
      considerAs: SalaryComponentConsiderAs.Earnings,
      isTaxable: false,
      isStatutory: false,
      isActive: true,
    });
    setDialogOpen(true);
  };

  const openEdit = (item: SalaryComponentDto) => {
    setEditItem(item);
    setErrors({});
    setForm({
      name: item.name,
      label: item.label,
      type: item.type,
      considerAs: item.considerAs,
      isTaxable: item.isTaxable,
      isStatutory: item.isStatutory,
      isActive: item.isActive,
    });
    setDialogOpen(true);
  };

  const validate = (): boolean => {
    const newErrors: FormErrors = {};
    if (!form.name.trim()) {
      newErrors.name = 'Name is required.';
    } else if (!/^[a-z0-9_]+$/.test(form.name.trim())) {
      newErrors.name = 'Use only lowercase letters, numbers, and underscores.';
    }

    if (!form.label.trim()) {
      newErrors.label = 'Label is required.';
    }

    if (form.type === null || form.type === undefined) {
      newErrors.type = 'Type is required.';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSave = async () => {
    if (!validate()) return;
    try {
      if (editItem) {
        await payrollApi.updateSalaryComponent(editItem.id, form as UpdateSalaryComponent);
        toast.success('Component updated');
      } else {
        await payrollApi.createSalaryComponent(form);
        toast.success('Component created');
      }
      setDialogOpen(false);
      void loadComponents();
    } catch (error) {
      toast.error(getSaveErrorMessage(error));
    }
  };

  return (
    <Box sx={{ p: 3 }}>
      <Stack direction="row" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h5" fontWeight={700}>Salary Components</Typography>
        <Button variant="contained" startIcon={<AddIcon />} onClick={openCreate} sx={{ textTransform: 'none' }}>
          Add Component
        </Button>
      </Stack>

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', pt: 4 }}>
          <CircularProgress />
        </Box>
      ) : (
        <Paper
          variant="outlined"
          sx={{
            overflow: 'hidden',
            borderColor: 'divider',
            boxShadow: '0 1px 2px rgba(15, 23, 42, 0.06)',
          }}
        >
          <Stack
            direction={{ xs: 'column', md: 'row' }}
            justifyContent="space-between"
            alignItems={{ xs: 'stretch', md: 'center' }}
            spacing={2}
            sx={{ px: 2.5, py: 2, borderBottom: '1px solid', borderColor: 'divider' }}
          >
            <Box>
             
              <Typography variant="body2" color="text.secondary">
                Total Records: {components.length}
                {searchTerm.trim() ? ` | Showing: ${filteredComponents.length}` : ''}
              </Typography>
            </Box>
            <TextField
              size="small"
              label="Search"
              placeholder="Name, label, type, status"
              value={searchTerm}
              onChange={(event) => setSearchTerm(event.target.value)}
              sx={{ minWidth: { xs: '100%', md: 320 } }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon fontSize="small" color="action" />
                  </InputAdornment>
                ),
              }}
            />
          </Stack>
          <TableContainer sx={{ maxHeight: 'calc(100vh - 310px)' }}>
            <Table>
              <TableHead
                sx={{
                  '& .MuiTableCell-head': {
                    bgcolor: 'grey.50',
                    fontWeight: 700,
                    color: 'text.primary',
                    borderBottom: '1px solid',
                    borderColor: 'divider',
                  },
                }}
              >
                <TableRow>
                  <TableCell>Name</TableCell>
                  <TableCell>Label</TableCell>
                  <TableCell>Type</TableCell>
                  <TableCell>Taxable</TableCell>
                  <TableCell>Statutory</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell align="center">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {filteredComponents.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={7} align="center" sx={{ py: 5, color: 'text.secondary' }}>
                      No salary components found
                    </TableCell>
                  </TableRow>
                ) : (
                  filteredComponents.map((comp) => (
                    <TableRow key={comp.id} hover>
                      <TableCell>{comp.name}</TableCell>
                      <TableCell>{comp.label}</TableCell>
                      <TableCell>
                        <Chip label={SalaryComponentTypeLabels[comp.type]} color={SalaryComponentTypeColors[comp.type]} size="small" />
                      </TableCell>
                      <TableCell>{comp.isTaxable ? 'Yes' : 'No'}</TableCell>
                      <TableCell>{comp.isStatutory ? 'Yes' : 'No'}</TableCell>
                      <TableCell>
                        <Chip label={comp.isActive ? 'Active' : 'Inactive'} color={comp.isActive ? 'success' : 'default'} size="small" />
                      </TableCell>
                      <TableCell align="center">
                        <Tooltip title="Edit">
                          <IconButton size="small" onClick={() => openEdit(comp)}>
                            <EditIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>
      )}

      {/* Create/Edit Dialog */}
      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>{editItem ? 'Edit Elements' : 'Add Elements'}</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <TextField
              label="Name"
              value={form.name}
              onChange={(e) => {
                setForm({ ...form, name: e.target.value });
                setErrors((prev) => ({ ...prev, name: undefined }));
              }}
              fullWidth
              required
              error={!!errors.name}
              helperText={errors.name ?? 'Use only lowercase letters and underscores. Example: name_this. Spaces and uppercase letters are not allowed.'}
            />
            <TextField
              label="Label"
              value={form.label}
              onChange={(e) => {
                setForm({ ...form, label: e.target.value });
                setErrors((prev) => ({ ...prev, label: undefined }));
              }}
              fullWidth
              required
              error={!!errors.label}
              helperText={errors.label ?? 'Enter the label for the element.'}
            />
            <TextField
              select
              label="Type"
              value={form.type}
              onChange={(e) => {
                const type = Number(e.target.value) as SalaryComponentType;
                setForm({
                  ...form,
                  type,
                  considerAs: getDefaultConsiderAs(type),
                  isTaxable: [SalaryComponentType.Deduction, SalaryComponentType.TaxComponent, SalaryComponentType.LeaveComponent].includes(type)
                    ? false
                    : form.isTaxable,
                });
                setErrors((prev) => ({ ...prev, type: undefined }));
              }}
              fullWidth
              required
              error={!!errors.type}
              helperText={errors.type ?? 'Select the type of the element.'}
            >
              {salaryComponentTypeOptions.map((option) => (
                <MenuItem key={option.value} value={option.value}>{option.label}</MenuItem>
              ))}
            </TextField>
            <FormControlLabel
              control={
                <Switch
                  checked={form.isTaxable}
                  onChange={(e) => setForm({ ...form, isTaxable: e.target.checked })}
                  disabled={[SalaryComponentType.Deduction, SalaryComponentType.TaxComponent, SalaryComponentType.LeaveComponent].includes(form.type)}
                />
              }
              label="Taxable"
            />
            <FormControlLabel
              control={<Switch checked={form.isStatutory} onChange={(e) => setForm({ ...form, isStatutory: e.target.checked })} />}
              label="Statutory"
            />
            <FormControlLabel
              control={<Switch checked={form.isActive} onChange={(e) => setForm({ ...form, isActive: e.target.checked })} />}
              label="Active"
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOpen(false)} sx={{ textTransform: 'none' }}>
            Cancel
          </Button>
          <Button variant="contained" onClick={handleSave} disabled={!form.name.trim() || !form.label.trim()} sx={{ textTransform: 'none' }}>
            {editItem ? 'Update' : 'Save'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default SalaryComponentsPage;
